import { CopyIcon, FlagIcon, MessageSquareIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useMemo } from 'react'

import { AssessmentInfo } from '@/gen/inventory/v1/assessment_service_pb'
import type { Company } from '@/gen/inventory/v1/company_service_pb'
import {
  ManualQuestionnaire,
  Questionnaire,
  QuestionnaireType,
} from '@/gen/questionnaire/v2/model_pb'

import { useHasPermissions } from '@/lib/app/check-permissions.hook'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { QuestionnairesHeader } from '@/pages/assessment/questionnaire/questionnaires-header'
import { useCopyQuestionnaireLink } from '@/pages/assessment/questionnaire/use-copy-questionnaire-link.hook'
import { useDeleteQuestionnaireModal } from '@/pages/assessment/questionnaire/use-delete-questionnaire-modal.hook'

import { useAddQuestionnaireModal } from '@/modals/add-questionnaire-modal/use-add-questionnaire-modal.hook'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { Button } from '@/components/lemmala-design-system/button/button'
import { Empty } from '@/components/lemmala-design-system/empty/empty'
import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import {
  NoRecipientsBadge,
  QuestionnaireStatusBadge,
} from '@/components/shared/questionnaire-status-badge/questionnaire-status-badge'

type QuestionnairesTabProps = {
  company: Company
  questionnaires: Questionnaire[]
  isLoading: boolean
  assessmentId: AssessmentInfo['id']
  dueDate?: AssessmentInfo['dueDate']
}

export const QuestionnairesTab = ({
  company,
  questionnaires,
  isLoading,
  assessmentId,
  dueDate,
}: QuestionnairesTabProps) => {
  const hasManagePermissions = useHasPermissions(['base.manage'])
  const {
    renderModal: renderHeaderAddQuestionnaireModal,
    openModal: showHeaderAddQuestionnaireModal,
  } = useAddQuestionnaireModal({
    companyId: company.id,
    ongoingAssessmentId: assessmentId,
    source: 'tab-header',
  })
  const {
    renderModal: renderEmptyStateAddQuestionnaireModal,
    openModal: showEmptyStateAddQuestionnaireModal,
  } = useAddQuestionnaireModal({
    companyId: company.id,
    ongoingAssessmentId: assessmentId,
    source: 'empty-state',
  })

  // Filter out smart evidence questionnaires (QUESTIONNAIRE_TYPE_RISK) and sort
  const sortedFilteredQuestionnaires = useMemo(
    () =>
      questionnaires
        .filter((q) => q.base?.type !== QuestionnaireType.RISK)
        .sort((a, b) => (a.base?.name ?? '').localeCompare(b.base?.name ?? '')),
    [questionnaires],
  )

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className='pb-6'>
      <QuestionnairesHeader
        hasQuestionnaires={sortedFilteredQuestionnaires.length > 0}
        company={company}
        readonly={!hasManagePermissions}
        dueDate={dueDate}
        assessmentId={assessmentId}
        className='border border-main-border-default bg-main-background-card'
      />

      <div className='mb-4 flex items-center justify-between'>
        <h2 className='text-heading-xxsmall'>Published questionnaires</h2>
        {hasManagePermissions && (
          <Button onClick={() => showHeaderAddQuestionnaireModal()} variant='ghost' size='sm'>
            <TextWithIcon text='Add questionnaire' icon={<PlusIcon className='size-4' />} />
          </Button>
        )}
      </div>

      {sortedFilteredQuestionnaires.length > 0 ? (
        <div className='grid grid-cols-[auto,auto,min-content,auto,min-content] gap-x-4 rounded border border-main-border-default'>
          {sortedFilteredQuestionnaires.map((questionnaire) => (
            <QuestionnaireRow
              key={questionnaire.base?.id}
              questionnaire={questionnaire}
              company={company}
            />
          ))}
        </div>
      ) : (
        <Empty
          icon='primary'
          title='No questionnaires published yet.'
          button={{
            label: 'Add questionnaire',
            onClick: () => showEmptyStateAddQuestionnaireModal(),
          }}
          className='rounded-md border border-main-border-default bg-main-background-card py-12'
        />
      )}

      {renderHeaderAddQuestionnaireModal()}
      {renderEmptyStateAddQuestionnaireModal()}
    </div>
  )
}

type QuestionnaireRowProps = {
  questionnaire: Questionnaire
  company: Company
}

const QuestionnaireRow = ({ questionnaire, company }: QuestionnaireRowProps) => {
  const { updateParam } = useUrlParams()
  const { copyQuestionnaireLink } = useCopyQuestionnaireLink()
  const manualQuestionnaire = questionnaire.questionnaire.value as ManualQuestionnaire
  const noRecipients = company.recipientsEmails.length === 0
  const hasManagePermissions = useHasPermissions(['base.manage'])

  const { showDeleteQuestionnaireModal } = useDeleteQuestionnaireModal({
    questionnaireId: questionnaire.base?.id ?? '',
    questionnaireName: questionnaire.base?.name ?? '',
    companyName: company.profile?.name ?? '',
  })

  const viewResponsesEnabled =
    !noRecipients || (manualQuestionnaire && manualQuestionnaire.totalAwaitingComments > 0)

  const handleCopyLink = () => {
    copyQuestionnaireLink(company.id, questionnaire.base?.id ?? '')
  }

  const handleViewResponses = () => {
    updateParam(UrlParam.QUESTIONNAIRE_ID, questionnaire.base?.id ?? '')
  }

  return (
    <>
      <div className='col-span-full grid grid-cols-subgrid items-center gap-4 rounded border-b border-main-border-default bg-main-background-card p-4 last:border-b-0'>
        <h4
          title={questionnaire.base?.name}
          className='truncate text-main-foreground-primary text-label-default'
        >
          {questionnaire.base?.name}
        </h4>

        <div>
          {noRecipients ? (
            <NoRecipientsBadge />
          ) : (
            <QuestionnaireStatusBadge questionnaire={questionnaire} />
          )}
        </div>

        <div className='flex items-center gap-2'>
          {manualQuestionnaire?.totalAwaitingComments > 0 && (
            <Badge badgeStyle='ghost' variant='warning'>
              <TextWithIcon
                text={manualQuestionnaire.totalAwaitingComments}
                icon={<MessageSquareIcon className='size-3.5' />}
              />
            </Badge>
          )}

          {manualQuestionnaire?.totalFlaggedAnswers > 0 && (
            <Badge variant='warning'>
              <TextWithIcon
                text={manualQuestionnaire.totalFlaggedAnswers}
                icon={<FlagIcon className='size-3.5' />}
              />
            </Badge>
          )}
        </div>

        <div className='flex items-center justify-end gap-4'>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleViewResponses}
            disabled={!viewResponsesEnabled}
          >
            Review responses
          </Button>

          <Tooltip
            trigger={
              <Button variant='ghost' size='icon-sm' onClick={handleCopyLink}>
                <CopyIcon className='size-4' />
              </Button>
            }
          >
            Copy third-party link
          </Tooltip>

          {hasManagePermissions && (
            <Tooltip
              trigger={
                <Button variant='ghost' size='icon-sm' onClick={showDeleteQuestionnaireModal}>
                  <Trash2Icon className='size-4' />
                </Button>
              }
            >
              Delete questionnaire
            </Tooltip>
          )}
        </div>
      </div>
    </>
  )
}
